import { motion } from 'framer-motion'
import { ArrowDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const images = [
  '/Hero.png',
  '/Hero1.png',
  '/Hero2.png',
  '/gallery/sudha2.png',
  '/Hero.png',
  '/Hero1.png',
]

const Column = ({ images, y = 0, duration = 20 }: { images: string[], y?: any, duration?: number }) => {
  return (
    <div className="relative w-full h-[150vh] -mt-[25vh] overflow-hidden opacity-80">
      <motion.div
        animate={{ y: y }}
        transition={{
          duration: duration,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop"
        }}
        className="flex flex-col gap-4"
      >
        {[...images, ...images, ...images].map((src, i) => (
          <div key={i} className="relative w-full aspect-[3/4] rounded-lg overflow-hidden">
            <img src={src} alt="Gallery" className="w-full h-full object-cover" />
          </div>
        ))}
      </motion.div>
    </div>
  )
}

export default function Hero() {
  const navigate = useNavigate()

  return (
    <section 
      className="relative h-screen bg-[#FDFBF7] dark:bg-neutral-950 overflow-hidden flex items-center justify-center transition-colors duration-500"
      aria-label="Welcome to Sudha's Academy"
      itemScope 
      itemType="https://schema.org/WPHeader"
    >
      {/* Marquee Background */}
      <div className="absolute inset-0 grid grid-cols-3 gap-4 px-4 md:px-12 opacity-50" aria-hidden="true">
        <Column images={images} y={['-50%', '0%']} duration={40} />
        <Column images={[...images].reverse()} y={['0%', '-50%']} duration={35} />
        <Column images={images} y={['-50%', '0%']} duration={45} />
      </div>

      {/* Gradient Overlay for Readability */}
      <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-[2px] transition-colors duration-500" aria-hidden="true" />

      {/* Central Content */}
      <div className="relative z-10 text-center max-w-4xl px-6">
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border border-white/40 dark:border-white/10 p-12 md:p-20 rounded-3xl shadow-2xl transition-colors duration-500"
          itemScope
          itemType="https://schema.org/EducationalOrganization"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-charcoal-900/30 dark:bg-white/30" aria-hidden="true" />
            <time 
              dateTime="2006" 
              className="text-charcoal-500 dark:text-neutral-400 font-mono text-xs uppercase tracking-[0.3em]"
              itemProp="foundingDate"
            >
              Est. 2006
            </time>
            <div className="h-[1px] w-12 bg-charcoal-900/30 dark:bg-white/30" aria-hidden="true" />
          </div>

          <h1 
            className="font-serif text-5xl md:text-8xl font-black text-charcoal-900 dark:text-white mb-8 leading-[0.9]"
            itemProp="name"
          >
            <span itemProp="alternateName">SUDHA'S</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-purple-600">
              ACADEMY
            </span>
          </h1>

          <p 
            className="text-charcoal-600 dark:text-neutral-300 text-lg md:text-xl max-w-lg mx-auto leading-relaxed mb-10"
            itemProp="description"
          >
            Where technical mastery meets artistic freedom. Join the next generation of elite makeup artists. India's premier beauty training institute with 18+ years of excellence.
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4" role="group" aria-label="Main actions">
            <button
              onClick={() => navigate('/user/register')}
              className="px-8 py-4 bg-charcoal-900 dark:bg-white text-white dark:text-charcoal-900 font-medium text-sm uppercase tracking-widest hover:bg-rose-500 dark:hover:bg-rose-400 transition-all duration-300 shadow-lg hover:shadow-rose-500/30 w-full md:w-auto rounded-lg"
              aria-label="Start your beauty training journey - Register now"
            >
              Start Journey
            </button>
            <button
              onClick={() => document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white dark:bg-transparent border border-charcoal-200 dark:border-white/20 text-charcoal-900 dark:text-white font-medium text-sm uppercase tracking-widest hover:bg-charcoal-50 dark:hover:bg-white/10 transition-all duration-300 w-full md:w-auto rounded-lg"
              aria-label="Browse our makeup and beauty courses"
            >
              View Courses
            </button>
          </div>
          
          {/* Hidden SEO content for AI crawlers */}
          <meta itemProp="url" content="https://sudhasacademy.com" />
          <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress" className="sr-only">
            <span itemProp="addressLocality">Chennai</span>,
            <span itemProp="addressRegion">Tamil Nadu</span>,
            <span itemProp="addressCountry">India</span>
          </span>
        </motion.article>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] text-charcoal-400 dark:text-neutral-500">Scroll</span>
        <ArrowDown className="w-4 h-4 text-charcoal-400 dark:text-neutral-500 animate-bounce" />
      </motion.div>
    </section>
  )
}
